import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { MatchItemDefinition } from '../logic/matchItems';
import { COLORS } from './theme';

const BUTTON_SIZE = 46;
const BUTTON_GAP = 8;

export interface ItemBarEntry {
  def: MatchItemDefinition;
  count: number;
}

/**
 * Row of in-match item buttons (GunBound-style), shown above the power bar
 * on the active player's turn. Clicking/tapping a button uses the item.
 */
export class ItemBar {
  container: Container;
  private onUse: (itemId: string) => void;
  private screenWidth: number;
  private screenHeight: number;
  private signature = '';
  private barWidth = 0;

  constructor(screenWidth: number, screenHeight: number, onUse: (itemId: string) => void) {
    this.container = new Container();
    this.container.visible = false;
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.onUse = onUse;
  }

  /** Rebuilds buttons only when counts/visibility actually change. */
  update(entries: ItemBarEntry[], visible: boolean, enabled = true): void {
    const signature = visible
      ? `${enabled ? 'on' : 'off'}|${entries.map((e) => `${e.def.id}:${e.count}`).join(',')}`
      : 'hidden';
    if (signature === this.signature) return;
    this.signature = signature;

    this.container.visible = visible && entries.length > 0;
    this.container.removeChildren().forEach((child) => child.destroy({ children: true }));
    if (!this.container.visible) return;

    this.barWidth = entries.length * BUTTON_SIZE + (entries.length - 1) * BUTTON_GAP;
    // Narrow (portrait) screens: the bottom is occupied by the d-pad and
    // jump/fire clusters, so the items live top-center under the wind
    // indicator (GunBound keeps items at the top too). Wide screens keep
    // them above the power bar.
    const y =
      this.screenWidth < 700
        ? 156 // clear of the (taller) top-left status panel + top chrome
        : this.screenHeight - 40 - BUTTON_SIZE - 14;
    this.container.position.set((this.screenWidth - this.barWidth) / 2, y);

    entries.forEach((entry, index) => {
      this.container.addChild(
        this.createButton(entry, index * (BUTTON_SIZE + BUTTON_GAP), enabled),
      );
    });
  }

  /** Keep the bar above the power bar when the viewport resizes. */
  resize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.signature = ''; // force re-layout on the next update
  }

  /** Hit test in screen coordinates (used to suppress camera drag). */
  containsPoint(screenX: number, screenY: number): boolean {
    if (!this.container.visible) return false;
    const { x, y } = this.container.position;
    return (
      screenX >= x && screenX <= x + this.barWidth && screenY >= y && screenY <= y + BUTTON_SIZE
    );
  }

  private createButton(entry: ItemBarEntry, offsetX: number, enabled: boolean): Container {
    const button = new Container();
    button.position.set(offsetX, 0);
    const usable = entry.count > 0 && enabled; // one item per turn

    const bg = new Graphics();
    bg.roundRect(0, 0, BUTTON_SIZE, BUTTON_SIZE, 8);
    bg.fill({ color: usable ? 0x1b2f54 : 0x141d33, alpha: 0.9 });
    bg.roundRect(2, 2, BUTTON_SIZE - 4, BUTTON_SIZE * 0.4, 6).fill({
      color: COLORS.white,
      alpha: usable ? 0.12 : 0.04,
    });
    bg.roundRect(0, 0, BUTTON_SIZE, BUTTON_SIZE, 8).stroke({
      width: 2,
      color: usable ? COLORS.gold : COLORS.stroke,
    });
    button.addChild(bg);

    const icon = new Text({
      text: entry.def.icon,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 20,
        fill: usable ? 0xffffff : 0x777777,
      }),
    });
    icon.anchor.set(0.5, 0.5);
    icon.position.set(BUTTON_SIZE / 2, BUTTON_SIZE / 2 - 5);
    button.addChild(icon);

    const name = new Text({
      text: entry.def.name,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 8,
        fill: usable ? 0xcccccc : 0x666666,
      }),
    });
    name.anchor.set(0.5, 1);
    name.position.set(BUTTON_SIZE / 2, BUTTON_SIZE - 4);
    button.addChild(name);

    const count = new Text({
      text: `${entry.count}`,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 11,
        fontWeight: 'bold',
        fill: usable ? 0xffee44 : 0x666666,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    count.anchor.set(1, 0);
    count.position.set(BUTTON_SIZE - 3, 2);
    button.addChild(count);

    if (usable) {
      button.eventMode = 'static';
      button.cursor = 'pointer';
      button.on('pointerdown', () => this.onUse(entry.def.id));
    }

    return button;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
